/**
 * Scenario Builder - Build, simulate, and analyze ESG impact scenarios
 * Tabbed results: Overview | Timeline | Sensitivity
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Play, X, Loader2,
  TrendingDown, DollarSign, Shield, Target, Zap, CheckCircle2,
  AlertTriangle, BarChart3, Leaf, Truck, Users,
  Droplets, Recycle, Info, FlaskConical, SlidersHorizontal, RotateCcw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend, Line, Cell
} from 'recharts';
import { apiFetch } from '../../services/api_service';

const CAT_ICONS = {
  energy: Zap, transportation: Truck, supply_chain: Users,
  offsets: Leaf, social: Users, waste: Recycle, water: Droplets,
};
const CAT_COLORS = {
  energy: ['text-amber-400', 'bg-amber-500/20', 'border-amber-500/30'],
  transportation: ['text-blue-400', 'bg-blue-500/20', 'border-blue-500/30'],
  supply_chain: ['text-purple-400', 'bg-purple-500/20', 'border-purple-500/30'],
  offsets: ['text-emerald-400', 'bg-emerald-500/20', 'border-emerald-500/30'],
  social: ['text-pink-400', 'bg-pink-500/20', 'border-pink-500/30'],
  waste: ['text-orange-400', 'bg-orange-500/20', 'border-orange-500/30'],
  water: ['text-cyan-400', 'bg-cyan-500/20', 'border-cyan-500/30'],
};

function fmt$(v) {
  if (!v && v !== 0) return '$0';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v)}`;
}
function fmtE(v) {
  if (!v && v !== 0) return '0';
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return `${v}`;
}

export default function ScenarioBuilderPage() {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [scenario, setScenario] = useState(null);
  const [actionLibrary, setActionLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libFilter, setLibFilter] = useState('all');
  const [resultTab, setResultTab] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sRes, aRes] = await Promise.all([
          apiFetch(`/v1/simulations/${scenarioId}`),
          apiFetch(`/v1/simulations/action-library`),
        ]);
        if (sRes && !sRes._error) setScenario(sRes);
        if (aRes && !aRes._error) {
          setActionLibrary(aRes.actions || []);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [scenarioId]);

  const addAction = async (actionId) => {
    try {
      const res = await apiFetch(`/v1/simulations/${scenarioId}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actionId, params: {} }),
      });
      if (res && !res._error) {
        const sRes = await apiFetch(`/v1/simulations/${scenarioId}`);
        if (sRes && !sRes._error) setScenario(sRes);
        toast({ title: 'Action Added' });
      }
    } catch {
      toast({ title: 'Error adding action', variant: 'destructive' });
    }
  };

  const removeAction = async (actionId) => {
    try {
      await apiFetch(`/v1/simulations/${scenarioId}/actions/${actionId}`, { method: 'DELETE' });
      const sRes = await apiFetch(`/v1/simulations/${scenarioId}`);
      if (sRes && !sRes._error) setScenario(sRes);
    } catch {}
  };

  const runSimulation = async () => {
    setSimulating(true);
    try {
      const res = await apiFetch(`/v1/simulations/${scenarioId}/run`, { method: 'POST' });
      if (res && !res._error) {
        const sRes = await apiFetch(`/v1/simulations/${scenarioId}`);
        if (sRes && !sRes._error) setScenario(sRes);
        toast({ title: 'Simulation Complete' });
        setResultTab('overview');
      }
    } catch {
      toast({ title: 'Simulation Failed', variant: 'destructive' });
    } finally {
      setSimulating(false);
    }
  };

  const commitScenario = async () => {
    if (!window.confirm('Commit this scenario? This will create obligations from the selected actions.')) return;
    try {
      const res = await apiFetch(`/v1/simulations/${scenarioId}/commit`, { method: 'POST' });
      if (res && !res._error) {
        const sRes = await apiFetch(`/v1/simulations/${scenarioId}`);
        if (sRes && !sRes._error) setScenario(sRes);
        toast({ title: 'Scenario Committed', description: 'Obligations have been created.' });
      }
    } catch {
      toast({ title: 'Commit Failed', variant: 'destructive' });
    }
  };

  const filteredLib = useMemo(() => {
    if (libFilter === 'all') return actionLibrary;
    return actionLibrary.filter(a => a.actionCategory === libFilter);
  }, [actionLibrary, libFilter]);

  const addedIds = useMemo(() => new Set((scenario?.actions || []).map(a => a.actionId)), [scenario]);

  const sr = scenario?.simulationRun;
  const fr = sr?.finalResults;
  const projections = sr?.yearlyProjections || [];

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-violet-400 animate-spin" /></div>;
  if (!scenario) return <div className="text-center py-20 text-[#6B6B6B]">Scenario not found</div>;

  const baseYear = scenario.baseline?.year || 2024;
  const targetYear = scenario.target?.targetYear || 2030;

  return (
    <div className="space-y-5" data-testid="scenario-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/simulations')} className="text-[#6B6B6B] hover:text-white transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">{scenario.scenarioName}</h1>
            <p className="text-xs text-[#6B6B6B]">
              Target: {scenario.target?.type?.replace(/_/g, ' ')} by {targetYear}
              {' '} | {scenario.actions?.length || 0} actions | Status: {scenario.status}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-[#2F2F2F] text-[#A0A0A0] hover:text-white" onClick={() => setShowLibrary(!showLibrary)} data-testid="toggle-library-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Action
          </Button>
          <Button
            onClick={runSimulation}
            disabled={simulating || (scenario.actions?.length || 0) === 0}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="run-simulation-btn"
          >
            {simulating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            {simulating ? 'Simulating...' : 'Run Simulation'}
          </Button>
          {scenario.status === 'simulated' && (
            <Button onClick={commitScenario} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="commit-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Commit
            </Button>
          )}
        </div>
      </div>

      {/* Action Library Panel */}
      {showLibrary && (
        <ActionLibraryPanel
          actions={filteredLib}
          addedIds={addedIds}
          libFilter={libFilter}
          setLibFilter={setLibFilter}
          onAdd={addAction}
          onClose={() => setShowLibrary(false)}
        />
      )}

      {/* Selected Actions with Timeline */}
      {(scenario.actions?.length || 0) > 0 && (
        <div data-testid="selected-actions">
          <h2 className="text-sm font-semibold text-white mb-2">Selected Actions ({scenario.actions.length})</h2>
          <div className="space-y-2">
            {scenario.actions.map(action => (
              <ActionTimelineCard
                key={action.actionId}
                action={action}
                baseYear={baseYear}
                targetYear={targetYear}
                onRemove={() => removeAction(action.actionId)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(scenario.actions?.length || 0) === 0 && !showLibrary && (
        <div className="text-center py-16 border border-dashed border-[#2F2F2F] rounded-xl">
          <Target className="w-10 h-10 mx-auto mb-3 text-[#2F2F2F]" />
          <h3 className="text-base font-semibold text-white mb-1">Build Your Scenario</h3>
          <p className="text-sm text-[#6B6B6B] mb-3">Select actions from the library to model your ESG strategy</p>
          <Button variant="outline" className="border-violet-500/30 text-violet-400" onClick={() => setShowLibrary(true)}>
            <Plus className="w-4 h-4 mr-1" /> Browse Action Library
          </Button>
        </div>
      )}

      {/* Simulation Results - Tabbed */}
      {fr && (
        <div data-testid="simulation-results">
          {/* Tab Navigation */}
          <div className="flex items-center gap-1 mb-4 border-b border-[#1F1F1F] pb-px" data-testid="results-tabs">
            {['overview', 'timeline', 'sensitivity', 'whatif'].map(tab => (
              <button
                key={tab}
                onClick={() => setResultTab(tab)}
                className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  resultTab === tab
                    ? 'text-violet-400 border-violet-500'
                    : 'text-[#6B6B6B] border-transparent hover:text-[#A0A0A0]'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'overview' && 'Overview'}
                {tab === 'timeline' && 'Year-by-Year Timeline'}
                {tab === 'sensitivity' && 'Sensitivity Analysis'}
                {tab === 'whatif' && 'What-If Analysis'}
              </button>
            ))}
          </div>

          {resultTab === 'overview' && <OverviewTab fr={fr} projections={projections} scenario={scenario} />}
          {resultTab === 'timeline' && <TimelineTab projections={projections} />}
          {resultTab === 'sensitivity' && <SensitivityTab sensitivity={sr.sensitivityAnalysis} baseCase={fr} />}
          {resultTab === 'whatif' && <WhatIfTab scenarioId={scenarioId} baseCase={fr} />}
        </div>
      )}
    </div>
  );
}

/* ─── Action Library Panel ─────────────────────────────────────────── */
function ActionLibraryPanel({ actions, addedIds, libFilter, setLibFilter, onAdd, onClose }) {
  const cats = ['all', 'energy', 'transportation', 'supply_chain', 'offsets', 'social', 'waste', 'water'];
  return (
    <div className="bg-[#111111] border border-violet-500/30 rounded-xl overflow-hidden" data-testid="action-library-panel">
      <div className="p-3 border-b border-[#1F1F1F] flex items-center justify-between">
        <h3 className="text-sm font-medium text-white">Action Library</h3>
        <button onClick={onClose} className="text-[#6B6B6B] hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-3 border-b border-[#1F1F1F] flex gap-1.5 flex-wrap">
        {cats.map(c => (
          <button key={c} onClick={() => setLibFilter(c)}
            className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${libFilter === c ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30' : 'bg-[#1F1F1F] text-[#6B6B6B] border border-transparent hover:text-[#A0A0A0]'}`}>
            {c === 'all' ? 'All' : c.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
      <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[320px] overflow-y-auto">
        {actions.map(action => {
          const Icon = CAT_ICONS[action.actionCategory] || Zap;
          const [textC, bgC] = CAT_COLORS[action.actionCategory] || ['text-[#6B6B6B]', 'bg-[#1F1F1F]'];
          const isAdded = addedIds.has(action.actionId);
          return (
            <div key={action.actionId} className={`p-2.5 bg-[#0D0D0D] border rounded-lg transition-all ${isAdded ? 'border-emerald-500/30 opacity-60' : 'border-[#1F1F1F] hover:border-violet-500/30'}`}>
              <div className="flex items-start gap-2 mb-1.5">
                <div className={`p-1 rounded ${bgC}`}><Icon className={`w-3 h-3 ${textC}`} /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-white truncate">{action.actionName}</p>
                  <p className="text-[10px] text-[#6B6B6B] line-clamp-2 mt-0.5">{action.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {action.reduction_pct > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
                    -{Math.round(action.reduction_pct * 100)}% {action.scope_impact}
                  </span>
                )}
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">
                  {fmt$(action.capex_estimate)} CAPEX
                </span>
              </div>
              <div className="flex items-center justify-end mt-2">
                {isAdded ? (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-[10px]">Added</Badge>
                ) : (
                  <button onClick={() => onAdd(action.actionId)} className="text-xs text-violet-400 hover:text-violet-300 font-medium" data-testid={`add-${action.actionId}`}>
                    + Add to Scenario
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Action Timeline Card ─────────────────────────────────────────── */
function ActionTimelineCard({ action, baseYear, targetYear, onRemove }) {
  const Icon = CAT_ICONS[action.actionCategory] || Zap;
  const [textC, bgC] = CAT_COLORS[action.actionCategory] || ['text-[#6B6B6B]', 'bg-[#1F1F1F]'];
  const range = targetYear - baseYear || 1;
  const startPct = Math.max(0, Math.min(100, ((action.startYear - baseYear) / range) * 100));
  const implPct = Math.max(startPct, Math.min(100, ((action.implementationYear - baseYear) / range) * 100));
  const barWidth = Math.max(2, implPct - startPct);

  return (
    <div className="p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg hover:border-[#2F2F2F] transition-colors" data-testid={`selected-${action.actionId}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2.5">
          <div className={`p-1.5 rounded-lg ${bgC}`}><Icon className={`w-4 h-4 ${textC}`} /></div>
          <div>
            <p className="text-sm font-medium text-white">{action.actionName}</p>
            <div className="flex gap-3 text-xs text-[#6B6B6B] mt-0.5">
              <span>{action.startYear} - {action.implementationYear}</span>
              <span>{action.emissionsImpact?.percentageReduction || 0}% reduction</span>
              <span>{fmt$(action.financialImpact?.capex || 0)} CAPEX</span>
              <span>CSRD: {action.complianceImpact?.csrdScoreChange > 0 ? '+' : ''}{action.complianceImpact?.csrdScoreChange || 0}</span>
            </div>
          </div>
        </div>
        <button onClick={onRemove} className="text-[#6B6B6B] hover:text-red-400 transition-colors" data-testid={`remove-${action.actionId}`}>
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* Visual Timeline Bar */}
      <div className="mt-2">
        <div className="h-6 bg-[#0D0D0D] rounded relative overflow-hidden">
          {/* Planning phase */}
          <div
            className="absolute h-full rounded opacity-40"
            style={{ left: `${startPct}%`, width: `${barWidth}%`, background: 'linear-gradient(90deg, #7c3aed33, #7c3aed66)' }}
          />
          {/* Active phase marker */}
          <div
            className="absolute h-full w-1 bg-violet-500 rounded"
            style={{ left: `${implPct}%` }}
          />
          {/* Label */}
          <div className="absolute inset-0 flex items-center px-2">
            <span className="text-[10px] text-[#6B6B6B]">{action.startYear}</span>
            <span className="ml-auto text-[10px] text-[#6B6B6B]">{action.implementationYear}</span>
          </div>
        </div>
        <div className="flex justify-between text-[9px] text-[#3F3F3F] mt-0.5 px-0.5">
          <span>{baseYear}</span>
          <span>{Math.round((baseYear + targetYear) / 2)}</span>
          <span>{targetYear}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Overview Tab ─────────────────────────────────────────────────── */
function OverviewTab({ fr, projections, scenario }) {
  const baseline = scenario.baseline;
  const baselineTotal = baseline?.emissions?.total || 0;

  return (
    <div className="space-y-5">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="results-summary">
        <MetricCard
          icon={Target}
          label="Net Zero Achievement"
          value={fr.emissionsReduction.achievesNetZero ? 'Yes' : 'No'}
          subtext={`${fr.emissionsReduction.percentageReduction}% reduction`}
          color={fr.emissionsReduction.achievesNetZero ? 'emerald' : 'amber'}
        />
        <MetricCard
          icon={DollarSign}
          label="Total Investment"
          value={fmt$(fr.financialSummary.totalCost)}
          subtext={`${fmt$(fr.financialSummary.costPerTonAvoided)}/tCO2e avoided`}
          color="blue"
        />
        <MetricCard
          icon={Shield}
          label="CSRD Score (Target Year)"
          value={`${fr.complianceScores.csrdScore}/100`}
          subtext={`CDP Rating: ${fr.complianceScores.cdpRating}`}
          color="purple"
        />
        <MetricCard
          icon={AlertTriangle}
          label="Risk Level"
          value={fr.riskAssessment.overallRisk}
          subtext={`${Math.round(fr.riskAssessment.probabilityOfSuccess * 100)}% success probability`}
          color={fr.riskAssessment.overallRisk === 'low' ? 'emerald' : fr.riskAssessment.overallRisk === 'medium' ? 'amber' : 'red'}
        />
      </div>

      {/* Emissions Reduction Breakdown */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5" data-testid="emissions-breakdown">
        <h3 className="text-sm font-semibold text-white mb-4">Emissions Reduction Breakdown</h3>
        <div className="grid grid-cols-2 gap-6 mb-5">
          <div>
            <p className="text-xs text-[#6B6B6B] mb-1">Baseline ({scenario.baseline?.year || 2024})</p>
            <p className="text-2xl font-bold text-white">{fmtE(baselineTotal)} <span className="text-sm font-normal text-[#6B6B6B]">tCO2e</span></p>
          </div>
          <div>
            <p className="text-xs text-[#6B6B6B] mb-1">Target Year ({scenario.target?.targetYear})</p>
            <p className="text-2xl font-bold text-emerald-400">{fmtE(fr.emissionsReduction.netEmissions)} <span className="text-sm font-normal text-[#6B6B6B]">tCO2e</span></p>
          </div>
        </div>
        {/* Visual Bar */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="h-8 bg-red-500/20 rounded-lg relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-red-300">
                  Baseline: {fmtE(baselineTotal)} tCO2e
                </div>
              </div>
            </div>
            <TrendingDown className="w-5 h-5 text-emerald-400 shrink-0" />
            <div className="flex-1">
              <div className="h-8 rounded-lg relative overflow-hidden" style={{ background: 'linear-gradient(90deg, #059669, #10B981)' }}>
                <div
                  className="absolute inset-y-0 left-0 bg-[#111111] rounded-l-lg"
                  style={{ width: `${Math.max(0, 100 - fr.emissionsReduction.percentageReduction)}%` }}
                />
                <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                  {fr.emissionsReduction.achievesNetZero ? 'Net Zero' : `${fmtE(fr.emissionsReduction.netEmissions)} tCO2e`}
                </div>
              </div>
            </div>
          </div>
          <p className="text-center text-xs text-[#6B6B6B]">
            {fr.emissionsReduction.percentageReduction}% reduction ({fmtE(fr.emissionsReduction.absoluteReduction)} tCO2e avoided)
          </p>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5" data-testid="financial-summary">
        <h3 className="text-sm font-semibold text-white mb-4">Financial Summary ({scenario.baseline?.year}-{scenario.target?.targetYear})</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 bg-violet-500/10 border border-violet-500/20 rounded-lg">
            <p className="text-xs text-[#A0A0A0] mb-1">CAPEX</p>
            <p className="text-xl font-bold text-violet-400">{fmt$(fr.financialSummary.capex)}</p>
            <p className="text-[10px] text-[#6B6B6B] mt-1">One-time investments</p>
          </div>
          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-xs text-[#A0A0A0] mb-1">OPEX</p>
            <p className="text-xl font-bold text-blue-400">{fmt$(fr.financialSummary.opex)}</p>
            <p className="text-[10px] text-[#6B6B6B] mt-1">Operating costs ({(scenario.target?.targetYear || 2030) - (scenario.baseline?.year || 2024)} years)</p>
          </div>
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
            <p className="text-xs text-[#A0A0A0] mb-1">Ongoing (Post-Target)</p>
            <p className="text-xl font-bold text-emerald-400">{fmt$(fr.financialSummary.ongoingAnnualCost || 0)}<span className="text-sm font-normal">/yr</span></p>
            <p className="text-[10px] text-[#6B6B6B] mt-1">Annual maintenance</p>
          </div>
        </div>
      </div>

      {/* Risk Assessment */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5" data-testid="risk-assessment">
        <h3 className="text-sm font-semibold text-white mb-4">Risk Assessment</h3>
        <div className="flex items-start gap-4">
          <div className={`w-16 h-16 rounded-xl flex items-center justify-center shrink-0 ${
            fr.riskAssessment.overallRisk === 'low' ? 'bg-emerald-500/20' :
            fr.riskAssessment.overallRisk === 'medium' ? 'bg-amber-500/20' : 'bg-red-500/20'
          }`}>
            <AlertTriangle className={`w-7 h-7 ${
              fr.riskAssessment.overallRisk === 'low' ? 'text-emerald-400' :
              fr.riskAssessment.overallRisk === 'medium' ? 'text-amber-400' : 'text-red-400'
            }`} />
          </div>
          <div className="flex-1">
            <p className="text-lg font-bold text-white capitalize">{fr.riskAssessment.overallRisk} Risk</p>
            <p className="text-xs text-[#6B6B6B] mb-3">{Math.round(fr.riskAssessment.probabilityOfSuccess * 100)}% probability of achieving targets</p>
            {fr.riskAssessment.keyRisks?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-[#A0A0A0] mb-1.5">Key Risks to Monitor:</p>
                <ul className="space-y-1">
                  {fr.riskAssessment.keyRisks.map((risk, i) => (
                    <li key={i} className="text-xs text-[#6B6B6B] flex items-start gap-1.5">
                      <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                      {risk}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Timeline Tab ─────────────────────────────────────────────────── */
function TimelineTab({ projections }) {
  return (
    <div className="space-y-5">
      {/* Emissions Trajectory Chart */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4" data-testid="timeline-chart">
        <h3 className="text-sm font-medium text-white mb-3">Emissions Trajectory</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={projections}>
            <defs>
              <linearGradient id="gradS1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradS2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradS3" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
            <XAxis dataKey="year" tick={{ fill: '#6B6B6B', fontSize: 11 }} />
            <YAxis tick={{ fill: '#6B6B6B', fontSize: 11 }} tickFormatter={v => fmtE(v)} />
            <Tooltip contentStyle={{ background: '#111', border: '1px solid #2F2F2F', borderRadius: 8, fontSize: 12 }} formatter={(v, name) => [`${fmtE(v)} tCO2e`, name]} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="emissions.scope1" name="Scope 1" stroke="#EF4444" fill="url(#gradS1)" strokeWidth={2} />
            <Area type="monotone" dataKey="emissions.scope2" name="Scope 2" stroke="#F59E0B" fill="url(#gradS2)" strokeWidth={2} />
            <Area type="monotone" dataKey="emissions.scope3" name="Scope 3" stroke="#8B5CF6" fill="url(#gradS3)" strokeWidth={2} />
            <Line type="monotone" dataKey="emissions.net" name="Net Total" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Cumulative Cost Chart */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4" data-testid="cost-chart">
        <h3 className="text-sm font-medium text-white mb-3">Cumulative Cost</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={projections}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
            <XAxis dataKey="year" tick={{ fill: '#6B6B6B', fontSize: 11 }} />
            <YAxis tick={{ fill: '#6B6B6B', fontSize: 11 }} tickFormatter={v => fmt$(v)} />
            <Tooltip contentStyle={{ background: '#111', border: '1px solid #2F2F2F', borderRadius: 8, fontSize: 12 }} formatter={(v) => [fmt$(v)]} />
            <Bar dataKey="cost.cumulative" name="Cumulative Cost" fill="#7c3aed" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Year-by-Year Table */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden" data-testid="yearly-table">
        <h3 className="text-sm font-medium text-white p-4 pb-2">Year-by-Year Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1F1F1F]">
                <th className="px-4 py-2.5 text-left text-xs font-medium text-[#6B6B6B]">Year</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[#6B6B6B]">Scope 1</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[#6B6B6B]">Scope 2</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[#6B6B6B]">Scope 3</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[#6B6B6B]">Net</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[#6B6B6B]">Annual Cost</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[#6B6B6B]">Cumulative</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[#6B6B6B]">CSRD</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-[#6B6B6B]">CDP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1F1F1F]">
              {projections.map((p, i) => (
                <tr key={p.year} className={`${i % 2 === 0 ? 'bg-[#111111]' : 'bg-[#0D0D0D]'} hover:bg-[#1A1A1A] transition-colors`}>
                  <td className="px-4 py-2.5 text-sm font-semibold text-white">{p.year}</td>
                  <td className="px-4 py-2.5 text-xs text-right text-red-400">{fmtE(p.emissions.scope1)}</td>
                  <td className="px-4 py-2.5 text-xs text-right text-amber-400">{fmtE(p.emissions.scope2)}</td>
                  <td className="px-4 py-2.5 text-xs text-right text-purple-400">{fmtE(p.emissions.scope3)}</td>
                  <td className="px-4 py-2.5 text-xs text-right font-bold text-emerald-400">{fmtE(p.emissions.net)}</td>
                  <td className="px-4 py-2.5 text-xs text-right text-[#A0A0A0]">{fmt$(p.cost.annual)}</td>
                  <td className="px-4 py-2.5 text-xs text-right text-blue-400 font-medium">{fmt$(p.cost.cumulative)}</td>
                  <td className="px-4 py-2.5 text-xs text-right text-[#A0A0A0]">{p.complianceScores.csrdScore}</td>
                  <td className="px-4 py-2.5 text-xs text-right text-[#A0A0A0]">{p.complianceScores.cdpRating}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── Sensitivity Tab ──────────────────────────────────────────────── */
function SensitivityTab({ sensitivity, baseCase }) {
  if (!sensitivity) return <p className="text-[#6B6B6B] text-sm">No sensitivity data available</p>;

  const bestCost = sensitivity.bestCase?.totalCost || 0;
  const baseCost = baseCase.financialSummary?.totalCost || 0;
  const worstCost = sensitivity.worstCase?.totalCost || 0;
  const costRange = worstCost - bestCost || 1;

  return (
    <div className="space-y-5">
      {/* Three-Scenario Comparison Table */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden" data-testid="sensitivity-panel">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1F1F1F]">
              <th className="px-5 py-3 text-left text-xs font-medium text-[#6B6B6B]">Scenario</th>
              <th className="px-5 py-3 text-right text-xs font-medium text-[#6B6B6B]">Total Cost</th>
              <th className="px-5 py-3 text-right text-xs font-medium text-[#6B6B6B]">Net Emissions</th>
              <th className="px-5 py-3 text-right text-xs font-medium text-[#6B6B6B]">CSRD Score</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-[#6B6B6B]">Assumptions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1F1F1F]">
            <tr className="bg-emerald-500/5 hover:bg-emerald-500/10">
              <td className="px-5 py-3.5 text-sm font-semibold text-emerald-400">Best Case</td>
              <td className="px-5 py-3.5 text-sm text-right text-emerald-400">{fmt$(bestCost)}</td>
              <td className="px-5 py-3.5 text-sm text-right text-emerald-300">{fmtE(sensitivity.bestCase?.netEmissions || 0)} tCO2e</td>
              <td className="px-5 py-3.5 text-sm text-right text-emerald-300">{sensitivity.bestCase?.csrdScore || '-'}</td>
              <td className="px-5 py-3.5 text-xs text-[#A0A0A0]">{sensitivity.bestCase?.description || 'Optimistic assumptions'}</td>
            </tr>
            <tr className="bg-blue-500/5 hover:bg-blue-500/10">
              <td className="px-5 py-3.5 text-sm font-semibold text-blue-400">Base Case (Most Likely)</td>
              <td className="px-5 py-3.5 text-sm text-right text-blue-400">{fmt$(baseCost)}</td>
              <td className="px-5 py-3.5 text-sm text-right text-blue-300">{fmtE(baseCase.emissionsReduction?.netEmissions || 0)} tCO2e</td>
              <td className="px-5 py-3.5 text-sm text-right text-blue-300">{baseCase.complianceScores?.csrdScore || '-'}</td>
              <td className="px-5 py-3.5 text-xs text-[#A0A0A0]">75% supplier success, $95/tCO2e offsets</td>
            </tr>
            <tr className="bg-red-500/5 hover:bg-red-500/10">
              <td className="px-5 py-3.5 text-sm font-semibold text-red-400">Worst Case</td>
              <td className="px-5 py-3.5 text-sm text-right text-red-400">{fmt$(worstCost)}</td>
              <td className="px-5 py-3.5 text-sm text-right text-red-300">{fmtE(sensitivity.worstCase?.netEmissions || 0)} tCO2e</td>
              <td className="px-5 py-3.5 text-sm text-right text-red-300">{sensitivity.worstCase?.csrdScore || '-'}</td>
              <td className="px-5 py-3.5 text-xs text-[#A0A0A0]">{sensitivity.worstCase?.description || 'Pessimistic assumptions'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Cost Range Visualization */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5" data-testid="cost-range">
        <h3 className="text-sm font-semibold text-white mb-4">Cost Range</h3>
        <div className="relative h-12 bg-[#0D0D0D] rounded-lg mb-2">
          {/* Gradient fill */}
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <div className="h-full" style={{ background: 'linear-gradient(90deg, rgba(16,185,129,0.15), rgba(59,130,246,0.15), rgba(239,68,68,0.15))' }} />
          </div>
          {/* Best marker */}
          <div className="absolute h-full flex flex-col items-center justify-start" style={{ left: '2%' }}>
            <div className="w-0.5 h-full bg-emerald-500" />
            <span className="text-[10px] text-emerald-400 font-medium mt-1 whitespace-nowrap">{fmt$(bestCost)}</span>
          </div>
          {/* Base marker */}
          <div className="absolute h-full flex flex-col items-center justify-start" style={{ left: `${Math.max(5, Math.min(95, ((baseCost - bestCost) / costRange) * 100))}%` }}>
            <div className="w-0.5 h-full bg-blue-500" />
            <span className="text-[10px] text-blue-400 font-medium mt-1 whitespace-nowrap">{fmt$(baseCost)}</span>
          </div>
          {/* Worst marker */}
          <div className="absolute h-full flex flex-col items-center justify-start" style={{ left: '96%' }}>
            <div className="w-0.5 h-full bg-red-500" />
            <span className="text-[10px] text-red-400 font-medium mt-1 whitespace-nowrap">{fmt$(worstCost)}</span>
          </div>
        </div>
        <p className="text-xs text-[#6B6B6B] text-center mt-6">
          Cost variance: {fmt$(worstCost - bestCost)} ({baseCost > 0 ? Math.round(((worstCost - bestCost) / baseCost) * 100) : 0}% of base case)
        </p>
      </div>

      {/* Recommendations */}
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5" data-testid="recommendations">
        <h3 className="text-sm font-semibold text-blue-400 mb-3 flex items-center gap-2">
          <Info className="w-4 h-4" />
          Recommendations
        </h3>
        <ul className="space-y-2">
          <li className="text-xs text-[#A0A0A0] flex items-start gap-2">
            <span className="text-blue-400 font-bold shrink-0">1.</span>
            Build supplier support budget (+{fmt$(Math.round((worstCost - baseCost) / 2))}) to mitigate supplier engagement risk
          </li>
          <li className="text-xs text-[#A0A0A0] flex items-start gap-2">
            <span className="text-blue-400 font-bold shrink-0">2.</span>
            Lock in long-term offset contracts to hedge against price volatility
          </li>
          <li className="text-xs text-[#A0A0A0] flex items-start gap-2">
            <span className="text-blue-400 font-bold shrink-0">3.</span>
            Accelerate operational reductions to minimize offset dependency
          </li>
          <li className="text-xs text-[#A0A0A0] flex items-start gap-2">
            <span className="text-blue-400 font-bold shrink-0">4.</span>
            Install on-site solar for EV charging to reduce grid emissions risk
          </li>
        </ul>
      </div>
    </div>
  );
}

/* ─── Metric Card ──────────────────────────────────────────────────── */
function MetricCard({ icon: Icon, label, value, subtext, color }) {
  const colorMap = {
    emerald: ['text-emerald-400', 'bg-emerald-500/10', 'border-emerald-500/20'],
    amber: ['text-amber-400', 'bg-amber-500/10', 'border-amber-500/20'],
    red: ['text-red-400', 'bg-red-500/10', 'border-red-500/20'],
    blue: ['text-blue-400', 'bg-blue-500/10', 'border-blue-500/20'],
    purple: ['text-purple-400', 'bg-purple-500/10', 'border-purple-500/20'],
  };
  const [textC, bgC, borderC] = colorMap[color] || colorMap.blue;

  return (
    <div className={`p-4 ${bgC} border ${borderC} rounded-xl`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${textC}`} />
        <span className="text-xs text-[#A0A0A0]">{label}</span>
      </div>
      <p className={`text-xl font-bold ${textC} capitalize`}>{value}</p>
      {subtext && <p className="text-xs text-[#6B6B6B] mt-1">{subtext}</p>}
    </div>
  );
}


/* ─── What-If Tab ──────────────────────────────────────────────────── */
const QUICK_WHATIF_TEMPLATES = [
  { id: 'pessimistic', name: 'Pessimistic Case', desc: 'Higher costs, lower performance', icon: AlertTriangle, color: 'red',
    overrides: { carbon_offset_price: 180, supplier_success_rate: 50, energy_cost_change: 130, ev_cost_change: 130, efficiency_gain_factor: 75, implementation_delay: 1 } },
  { id: 'optimistic', name: 'Optimistic Case', desc: 'Lower costs, higher performance', icon: TrendingDown, color: 'emerald',
    overrides: { carbon_offset_price: 50, supplier_success_rate: 90, energy_cost_change: 80, ev_cost_change: 75, efficiency_gain_factor: 125, implementation_delay: -1 } },
  { id: 'budget_cut', name: 'Budget Cut (-30%)', desc: 'Reduced budget, extended timeline', icon: DollarSign, color: 'amber',
    overrides: { implementation_delay: 2, efficiency_gain_factor: 75, supplier_success_rate: 60 } },
  { id: 'offset_crisis', name: 'Offset Market Crisis', desc: 'Offset prices spike to $220/tCO2e', icon: AlertTriangle, color: 'orange',
    overrides: { carbon_offset_price: 220 } },
  { id: 'supplier_fail', name: 'Supplier Engagement Fails', desc: 'Only 40% of suppliers succeed', icon: Users, color: 'red',
    overrides: { supplier_success_rate: 40 } },
  { id: 'tech_breakthrough', name: 'Technology Breakthrough', desc: 'EV & renewable costs drop fast', icon: Zap, color: 'cyan',
    overrides: { energy_cost_change: 70, ev_cost_change: 65 } },
];

function WhatIfTab({ scenarioId, baseCase }) {
  const [params, setParams] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [showCreator, setShowCreator] = useState(false);
  const [running, setRunning] = useState(null);

  useEffect(() => {
    apiFetch(`/v1/simulations/what-if/parameters`)
      .then(d => { if (d && !d._error) setParams(d.parameters || []); })
      .catch(() => {});
  }, []);

  const runWhatIf = async (idx) => {
    const s = scenarios[idx];
    setRunning(idx);
    try {
      const overrides = {};
      s.parameters.forEach(p => {
        if (p.currentValue !== p.baseValue) overrides[p.id] = p.currentValue;
      });
      const res = await apiFetch(`/v1/simulations/${scenarioId}/what-if`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ whatIfName: s.name, parameterOverrides: overrides }),
      });
      if (res && !res._error) {
        setScenarios(prev => prev.map((sc, i) => i === idx ? { ...sc, results: res.results } : sc));
      }
    } catch {} finally { setRunning(null); }
  };

  const deleteWhatIf = (idx) => setScenarios(prev => prev.filter((_, i) => i !== idx));

  const addQuickTemplate = (tmpl) => {
    if (!params) return;
    const parameters = params.map(p => ({
      ...p,
      currentValue: tmpl.overrides[p.id] !== undefined ? tmpl.overrides[p.id] : p.baseValue,
    }));
    const newScenario = { id: `wi_${Date.now()}`, name: tmpl.name, description: tmpl.desc, parameters, results: null };
    setScenarios(prev => [...prev, newScenario]);
  };

  const completedScenarios = scenarios.filter(s => s.results);

  if (!params) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-violet-400 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="whatif-tab">
      {/* Base Case Reference */}
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4" data-testid="whatif-base-case">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-medium text-blue-400">Base Case (Original Scenario)</h3>
        </div>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <p className="text-[10px] text-[#6B6B6B]">Emissions Reduction</p>
            <p className="text-lg font-bold text-blue-400">{baseCase?.emissionsReduction?.percentageReduction || 0}%</p>
          </div>
          <div>
            <p className="text-[10px] text-[#6B6B6B]">Total Cost</p>
            <p className="text-lg font-bold text-blue-400">{fmt$(baseCase?.financialSummary?.totalCost || 0)}</p>
          </div>
          <div>
            <p className="text-[10px] text-[#6B6B6B]">Net Zero</p>
            <p className="text-lg font-bold text-blue-400">{baseCase?.emissionsReduction?.achievesNetZero ? 'Yes' : 'No'}</p>
          </div>
          <div>
            <p className="text-[10px] text-[#6B6B6B]">Risk Level</p>
            <p className="text-lg font-bold text-blue-400 capitalize">{baseCase?.riskAssessment?.overallRisk || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Quick Templates */}
      <div data-testid="quick-templates">
        <h3 className="text-xs font-semibold text-[#A0A0A0] mb-2">QUICK TEMPLATES</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {QUICK_WHATIF_TEMPLATES.map(tmpl => {
            const Icon = tmpl.icon;
            const colorMap = { red: 'text-red-400 bg-red-500/10 border-red-500/20', emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20', orange: 'text-orange-400 bg-orange-500/10 border-orange-500/20', cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20' };
            const cls = colorMap[tmpl.color] || colorMap.amber;
            const isAdded = scenarios.some(s => s.name === tmpl.name);
            return (
              <button
                key={tmpl.id}
                onClick={() => !isAdded && addQuickTemplate(tmpl)}
                disabled={isAdded}
                className={`text-left p-3 rounded-lg border transition-all ${isAdded ? 'opacity-40 cursor-default border-[#1F1F1F]' : `${cls} hover:opacity-80 cursor-pointer`}`}
                data-testid={`quick-${tmpl.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="text-xs font-medium text-white truncate">{tmpl.name}</span>
                </div>
                <p className="text-[10px] text-[#6B6B6B] line-clamp-1">{tmpl.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white">Custom What-If Scenarios ({scenarios.length})</h2>
          <p className="text-xs text-[#6B6B6B]">Use quick templates above or create your own</p>
        </div>
        <button
          onClick={() => setShowCreator(true)}
          className="px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
          data-testid="new-whatif-btn"
        >
          <FlaskConical className="w-3.5 h-3.5" /> Custom What-If
        </button>
      </div>

      {/* Empty State */}
      {scenarios.length === 0 && !showCreator && (
        <div className="text-center py-8 border border-dashed border-[#2F2F2F] rounded-xl" data-testid="whatif-empty">
          <FlaskConical className="w-7 h-7 mx-auto mb-2 text-[#2F2F2F]" />
          <p className="text-xs text-[#6B6B6B]">Pick a quick template above or create a custom what-if</p>
        </div>
      )}

      {/* What-If Scenario Cards */}
      {scenarios.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="whatif-list">
          {scenarios.map((s, idx) => (
            <WhatIfCard key={s.id} scenario={s} baseCase={baseCase} onRun={() => runWhatIf(idx)} onDelete={() => deleteWhatIf(idx)} isRunning={running === idx} />
          ))}
        </div>
      )}

      {/* Comparison Summary - shown when 2+ what-ifs have results */}
      {completedScenarios.length >= 2 && (
        <WhatIfComparisonSummary baseCase={baseCase} whatIfs={completedScenarios} />
      )}

      {/* Creator Dialog */}
      {showCreator && (
        <WhatIfCreator
          params={params}
          onSave={(s) => { setScenarios(prev => [...prev, s]); setShowCreator(false); }}
          onCancel={() => setShowCreator(false)}
        />
      )}
    </div>
  );
}

/* ─── What-If Card ─────────────────────────────────────────────────── */
function WhatIfCard({ scenario: s, baseCase, onRun, onDelete, isRunning }) {
  const changed = s.parameters.filter(p => p.currentValue !== p.baseValue);
  const hasResults = !!s.results;
  const basePct = baseCase?.emissionsReduction?.percentageReduction || 0;
  const baseCost = baseCase?.financialSummary?.totalCost || 0;

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 hover:border-[#2F2F2F] transition-colors" data-testid={`whatif-card-${s.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="text-sm font-medium text-white">{s.name}</h4>
          {s.description && <p className="text-[10px] text-[#6B6B6B] mt-0.5">{s.description}</p>}
        </div>
        <button onClick={onDelete} className="text-[#6B6B6B] hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
      </div>

      {/* Changed Parameters */}
      <div className="mb-3">
        <p className="text-[10px] font-medium text-[#A0A0A0] mb-1">Modified ({changed.length})</p>
        <div className="space-y-0.5">
          {changed.map(p => {
            const delta = p.baseValue !== 0 ? Math.round(((p.currentValue - p.baseValue) / p.baseValue) * 100) : 0;
            return (
              <div key={p.id} className="flex items-center justify-between text-[10px] bg-amber-500/5 border border-amber-500/10 rounded px-2 py-1">
                <span className="text-[#A0A0A0]">{p.name}</span>
                <span className="text-white font-medium">
                  {p.baseValue} → {p.currentValue} {p.unit}
                  {delta !== 0 && <span className={delta > 0 ? 'text-red-400 ml-1' : 'text-emerald-400 ml-1'}>({delta > 0 ? '+' : ''}{delta}%)</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Results Comparison */}
      {hasResults ? (
        <div className="bg-[#0D0D0D] rounded-lg p-3">
          <p className="text-[10px] font-medium text-[#A0A0A0] mb-2">Results vs Base Case</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[9px] text-[#6B6B6B]">Emissions</p>
              {(() => {
                const resPct = s.results?.emissionsReduction?.percentageReduction || 0;
                const diff = resPct - basePct;
                return <p className={`text-sm font-bold ${diff >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {resPct}% <span className="text-[10px] font-normal">({diff >= 0 ? '+' : ''}{diff}%)</span>
                </p>;
              })()}
            </div>
            <div>
              <p className="text-[9px] text-[#6B6B6B]">Cost</p>
              {(() => {
                const resCost = s.results?.financialSummary?.totalCost || 0;
                const diff = resCost - baseCost;
                return <p className={`text-sm font-bold ${diff <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {fmt$(resCost)} <span className="text-[10px] font-normal">({diff >= 0 ? '+' : ''}{fmt$(diff)})</span>
                </p>;
              })()}
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={onRun}
          disabled={isRunning}
          className="w-full py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors"
          data-testid={`run-whatif-${s.id}`}
        >
          {isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          {isRunning ? 'Simulating...' : 'Run Simulation'}
        </button>
      )}
    </div>
  );
}

/* ─── What-If Creator ──────────────────────────────────────────────── */
function WhatIfCreator({ params, onSave, onCancel }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [values, setValues] = useState(() => {
    const init = {};
    params.forEach(p => { init[p.id] = p.baseValue; });
    return init;
  });

  const changedCount = params.filter(p => values[p.id] !== p.baseValue).length;
  const categories = { costs: 'Cost Assumptions', performance: 'Performance Assumptions', external: 'External Factors' };

  const handleSave = () => {
    const parameters = params.map(p => ({ ...p, currentValue: values[p.id] }));
    onSave({
      id: `wi_${Date.now()}`,
      name: name || 'Unnamed What-If',
      description,
      parameters,
      results: null,
    });
  };

  const resetAll = () => {
    const init = {};
    params.forEach(p => { init[p.id] = p.baseValue; });
    setValues(init);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="whatif-creator-modal">
      <div className="bg-[#111111] border border-[#2F2F2F] rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-[#1F1F1F] px-5 py-4">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-violet-400" />
            Create What-If Scenario
          </h3>
          <p className="text-xs text-[#6B6B6B] mt-0.5">Adjust assumptions to test scenario robustness</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Name & Description */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#A0A0A0] mb-1">Scenario Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Pessimistic - High Costs"
                className="w-full px-3 py-2 bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg text-sm text-white placeholder:text-[#3F3F3F] focus:outline-none focus:border-violet-500/50"
                data-testid="whatif-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#A0A0A0] mb-1">Description</label>
              <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g., Offset prices spike to $200/ton"
                className="w-full px-3 py-2 bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg text-sm text-white placeholder:text-[#3F3F3F] focus:outline-none focus:border-violet-500/50"
                data-testid="whatif-desc-input" />
            </div>
          </div>

          {/* Parameter Sliders by Category */}
          {Object.entries(categories).map(([catId, catName]) => {
            const catParams = params.filter(p => p.category === catId);
            if (catParams.length === 0) return null;
            return (
              <div key={catId}>
                <h4 className="text-xs font-semibold text-[#A0A0A0] mb-2 flex items-center gap-1.5">
                  <SlidersHorizontal className="w-3 h-3" />
                  {catName}
                </h4>
                <div className="space-y-3">
                  {catParams.map(p => {
                    const isChanged = values[p.id] !== p.baseValue;
                    const pctDelta = p.baseValue !== 0 ? Math.round(((values[p.id] - p.baseValue) / p.baseValue) * 100) : 0;
                    return (
                      <div key={p.id} className={`p-3 rounded-lg border transition-colors ${isChanged ? 'border-amber-500/30 bg-amber-500/5' : 'border-[#1F1F1F] bg-[#0D0D0D]'}`}>
                        <div className="flex items-start justify-between mb-1.5">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-xs font-medium text-white">{p.name}</p>
                              {isChanged && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-medium">{pctDelta > 0 ? '+' : ''}{pctDelta}%</span>}
                            </div>
                            <p className="text-[10px] text-[#6B6B6B]">{p.description}</p>
                          </div>
                          <div className="text-right shrink-0 ml-3">
                            <p className="text-sm font-bold text-white">{values[p.id]} {p.unit}</p>
                            <p className="text-[9px] text-[#3F3F3F]">Base: {p.baseValue} {p.unit}</p>
                          </div>
                        </div>
                        <input
                          type="range" min={p.min} max={p.max} step={p.step} value={values[p.id]}
                          onChange={e => setValues(prev => ({ ...prev, [p.id]: parseFloat(e.target.value) }))}
                          className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-violet-500"
                          style={{ background: `linear-gradient(to right, #7c3aed ${((values[p.id] - p.min) / (p.max - p.min)) * 100}%, #1F1F1F ${((values[p.id] - p.min) / (p.max - p.min)) * 100}%)` }}
                          data-testid={`slider-${p.id}`}
                        />
                        <div className="flex justify-between text-[9px] text-[#3F3F3F] mt-0.5">
                          <span>{p.min} {p.unit}</span>
                          <span>{p.max} {p.unit}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-[#1F1F1F] px-5 py-3 flex items-center justify-between">
          <button onClick={resetAll} className="text-xs text-[#6B6B6B] hover:text-[#A0A0A0] flex items-center gap-1 transition-colors">
            <RotateCcw className="w-3 h-3" /> Reset All
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className="px-3 py-1.5 rounded-lg border border-[#2F2F2F] text-[#A0A0A0] text-xs hover:text-white transition-colors" data-testid="whatif-cancel-btn">Cancel</button>
            <button
              onClick={handleSave}
              disabled={changedCount === 0}
              className="px-4 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium disabled:opacity-50 transition-colors"
              data-testid="whatif-create-btn"
            >
              Create What-If ({changedCount} change{changedCount !== 1 ? 's' : ''})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ─── What-If Comparison Summary ───────────────────────────────────── */
function WhatIfComparisonSummary({ baseCase, whatIfs }) {
  const basePct = baseCase?.emissionsReduction?.percentageReduction || 0;
  const baseCost = baseCase?.financialSummary?.totalCost || 0;

  // Build chart data
  const allCases = [
    { name: 'Base Case', pct: basePct, cost: baseCost, nz: baseCase?.emissionsReduction?.achievesNetZero },
    ...whatIfs.map(w => ({
      name: w.name,
      pct: w.results?.emissionsReduction?.percentageReduction || 0,
      cost: w.results?.financialSummary?.totalCost || 0,
      nz: w.results?.emissionsReduction?.achievesNetZero,
    })),
  ];

  const bestEm = allCases.reduce((a, b) => b.pct > a.pct ? b : a);
  const cheapest = allCases.reduce((a, b) => b.cost < a.cost ? b : a);
  const nzCount = allCases.filter(c => c.nz).length;

  const barColors = ['#3B82F6', '#A855F7', '#F59E0B', '#EF4444', '#10B981', '#EC4899', '#06B6D4'];

  return (
    <div className="space-y-4" data-testid="whatif-comparison">
      <h3 className="text-sm font-semibold text-white">Comparison Summary</h3>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
          <p className="text-[10px] text-[#6B6B6B]">Best Emissions Reduction</p>
          <p className="text-lg font-bold text-emerald-400">{bestEm.pct}%</p>
          <p className="text-[9px] text-[#6B6B6B]">{bestEm.name}</p>
        </div>
        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
          <p className="text-[10px] text-[#6B6B6B]">Lowest Cost</p>
          <p className="text-lg font-bold text-blue-400">{fmt$(cheapest.cost)}</p>
          <p className="text-[9px] text-[#6B6B6B]">{cheapest.name}</p>
        </div>
        <div className="p-3 bg-violet-500/10 border border-violet-500/20 rounded-xl">
          <p className="text-[10px] text-[#6B6B6B]">Net Zero Achievers</p>
          <p className="text-lg font-bold text-violet-400">{nzCount}/{allCases.length}</p>
          <p className="text-[9px] text-[#6B6B6B]">scenarios</p>
        </div>
      </div>

      {/* Emissions Comparison Bar Chart */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
        <h4 className="text-xs font-medium text-[#A0A0A0] mb-3">Emissions Reduction Comparison</h4>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={allCases} layout="vertical" margin={{ left: 90, right: 20, top: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" horizontal={false} />
            <XAxis type="number" domain={[0, 100]} tick={{ fill: '#6B6B6B', fontSize: 10 }} tickFormatter={v => `${v}%`} />
            <YAxis type="category" dataKey="name" tick={{ fill: '#A0A0A0', fontSize: 10 }} width={85} />
            <Tooltip contentStyle={{ background: '#111', border: '1px solid #2F2F2F', borderRadius: 8, fontSize: 11 }} formatter={v => [`${v}%`, 'Reduction']} />
            <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
              {allCases.map((_, i) => (
                <Cell key={i} fill={barColors[i % barColors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Cost Comparison Bar Chart */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
        <h4 className="text-xs font-medium text-[#A0A0A0] mb-3">Total Cost Comparison</h4>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={allCases} layout="vertical" margin={{ left: 90, right: 20, top: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" horizontal={false} />
            <XAxis type="number" tick={{ fill: '#6B6B6B', fontSize: 10 }} tickFormatter={v => fmt$(v)} />
            <YAxis type="category" dataKey="name" tick={{ fill: '#A0A0A0', fontSize: 10 }} width={85} />
            <Tooltip contentStyle={{ background: '#111', border: '1px solid #2F2F2F', borderRadius: 8, fontSize: 11 }} formatter={v => [fmt$(v), 'Cost']} />
            <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
              {allCases.map((_, i) => (
                <Cell key={i} fill={barColors[i % barColors.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Comparison Table */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden" data-testid="whatif-comparison-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1F1F1F]">
              <th className="px-4 py-2.5 text-left text-[10px] font-medium text-[#6B6B6B]">Scenario</th>
              <th className="px-4 py-2.5 text-right text-[10px] font-medium text-[#6B6B6B]">Reduction</th>
              <th className="px-4 py-2.5 text-right text-[10px] font-medium text-[#6B6B6B]">Cost</th>
              <th className="px-4 py-2.5 text-center text-[10px] font-medium text-[#6B6B6B]">Net Zero</th>
              <th className="px-4 py-2.5 text-right text-[10px] font-medium text-[#6B6B6B]">vs Base Cost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1F1F1F]">
            {allCases.map((c, i) => {
              const costDiff = c.cost - baseCost;
              return (
                <tr key={i} className={`${i === 0 ? 'bg-blue-500/5' : 'hover:bg-[#1A1A1A]'} transition-colors`}>
                  <td className="px-4 py-2.5 text-xs font-medium text-white">{c.name}</td>
                  <td className="px-4 py-2.5 text-xs text-right text-emerald-400 font-semibold">{c.pct}%</td>
                  <td className="px-4 py-2.5 text-xs text-right text-[#A0A0A0]">{fmt$(c.cost)}</td>
                  <td className="px-4 py-2.5 text-xs text-center">{c.nz ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mx-auto" /> : <X className="w-3.5 h-3.5 text-red-400 mx-auto" />}</td>
                  <td className={`px-4 py-2.5 text-xs text-right font-medium ${i === 0 ? 'text-[#3F3F3F]' : costDiff > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {i === 0 ? '—' : `${costDiff >= 0 ? '+' : ''}${fmt$(costDiff)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
